import { Injectable, inject } from "@angular/core";
import { LongOperationService } from "../auth/long-operation.service";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ContactsAccessType } from "../interfaces/contacts-access-type.enum";
import { AuthService } from "../auth/auth.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { isTaxprepDashboard } from "@app/views-interfaces";
import { concatMap, firstValueFrom, map, range, toArray } from "rxjs";

export interface IPayload {
    client_of?: number | null;
    manager_id?: number | null;
    group_filtering_id1?: number | null;
    group_filtering_id2?: number | null;
}

export interface IFirmContactInformationUpdate {
    contactId: number;
    payload: IPayload;
    deliveryMethod?: string | null;
}

@Injectable({
    providedIn: "root",
})
export class IFirmContactInformationService {
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly longOperationService = inject(LongOperationService);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly contactBatchSize = 500;
    private updateQueue: Promise<void> = Promise.resolve();

    async updateContacts(payload: IFirmContactInformationUpdate[]) {
        if (payload.length === 0) {
            return [];
        }

        const contacts = structuredClone(payload);
        const update = this.updateQueue.then(() => this.longOperationService.run(() => this.updateContactBatches(contacts)));
        this.updateQueue = update.then(
            () => undefined,
            () => undefined,
        );
        return update;
    }

    private updateContactBatches(payload: IFirmContactInformationUpdate[]) {
        return firstValueFrom(
            range(0, Math.ceil(payload.length / this.contactBatchSize)).pipe(
                concatMap((batchIndex) => {
                    const start = batchIndex * this.contactBatchSize;
                    return this.iframeMessagesService
                        .postMessageRespObservable<
                            { payload: IFirmContactInformationUpdate[] },
                            { status: string; response: unknown }
                        >("updateiFirmContactsInformation", "iFirmContactsInformationUpdated", { payload: payload.slice(start, start + this.contactBatchSize) }, 300000)
                        .pipe(
                            map((result) => {
                                if (result.status !== "success") {
                                    throw new Error("Failed to update contact information");
                                }
                                return result.response;
                            }),
                        );
                }),
                toArray(),
            ),
        );
    }

    hasAccess(access: ContactsAccessType): boolean {
        const currentUser = this.authService.currentUser();
        const currentDashboard = this.dashboardsService.currentDashboard();
        switch (access) {
            case ContactsAccessType.ContactAssignees:
                return (
                    isTaxprepDashboard(currentDashboard) ||
                    currentUser?.can_contacts_view_add_edit === "True" ||
                    currentUser?.can_edit_assignee === "True"
                );
            case ContactsAccessType.ContactGroups:
                return (
                    isTaxprepDashboard(currentDashboard) ||
                    currentUser?.can_contacts_view_add_edit === "True" ||
                    currentUser?.can_contacts_edit_group === "True"
                );
            case ContactsAccessType.ContactGenericInformations:
                return (
                    isTaxprepDashboard(currentDashboard) ||
                    currentUser?.can_contacts_view_add_edit === "True" ||
                    currentUser?.can_contacts_edit_generic_informations === "True"
                );
        }
    }
}
